import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../../utils/axiosInstance';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useMediaQuery,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

// dayjs 한국어 설정
dayjs.locale('ko');

const StatisticAgent = () => {
  const navigate = useNavigate();
  const isNonMobile = useMediaQuery('(min-width:600px)');

  // 필터 상태
  const [filters, setFilters] = useState({
    agentId: '',
    includeChildren: false,
    startDate: dayjs().subtract(7, 'day').startOf('day'),
    endDate: dayjs().endOf('day'),
  });

  const [loading, setLoading] = useState(false);
  const [loadingAgentList, setLoadingAgentList] = useState(false);
  
  // 통계 상태
  const [stats, setStats] = useState({
    total_summary: {
      bet_count: 0,
      total_bet: 0,
      total_win: 0,
      total_profit: 0,
    },
    game_type_stats: [],
    game_vendor_stats: [],
    agent_stats: [],
  });
  
  // 에이전트 베팅 리스트 데이터
  const [agentBettingList, setAgentBettingList] = useState({
    agents_list: []
  });

  const [agentList, setAgentList] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  
  // 페이지네이션 상태 (agent_stats 용)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // 에이전트 베팅 리스트 페이지네이션
  const [agentListPage, setAgentListPage] = useState(0);
  const [agentListRowsPerPage, setAgentListRowsPerPage] = useState(10);
  
  // 정렬 상태
  const [sortConfig, setSortConfig] = useState({
    key: 'total_bet',
    direction: 'desc'
  });

  // 현재 로그인한 사용자 정보 가져오기
  const getUser = () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  };

  // 에이전트 목록 불러오기
  useEffect(() => {
    const fetchAgentList = async () => {
      try {
        const user = getUser();
        if (user) {
          setCurrentUser(user);
          setFilters(prev => ({
            ...prev,
            agentId: user.id,
          }));
        }

        const response = await axios.get('/agent/children');
        if (response.data.success) {
          const agentTree = response.data.agent_tree;
          const agentOptions = [
            { id: agentTree.id, name: agentTree.id, level: 0 },
            ...agentTree.children.map(child => ({
              id: child.id,
              name: child.id,
              level: child.level,
            })),
          ];
          setAgentList(agentOptions);
        }
      } catch (error) {
        console.error('에이전트 목록 불러오기 중 오류 발생:', error);
      }
    };

    fetchAgentList();
  }, []);

  // 통계 데이터 가져오기
  const fetchStatistics = async () => {
    if (!filters.agentId) {
      return;
    }

    setLoading(true);
    try {
      const startDateFormatted = filters.startDate
        ? filters.startDate.format('YYYY-MM-DD')
        : dayjs().subtract(7, 'day').format('YYYY-MM-DD');

      const endDateFormatted = filters.endDate
        ? filters.endDate.format('YYYY-MM-DD')
        : dayjs().format('YYYY-MM-DD');

      console.log('통계 요청 파라미터:', {
        agent_id: filters.agentId,
        start: startDateFormatted,
        end: endDateFormatted,
        include_children: filters.includeChildren
      });

      const response = await axios.get('/agent/betting/statistics', {
        params: {
          agent_id: filters.agentId,
          start: startDateFormatted,
          end: endDateFormatted,
          include_children: filters.includeChildren || false
        },
      });

      console.log('통계 API 응답 데이터:', response.data);

      if (response.data.success) {
        setStats(response.data);
      } else {
        console.error('통계 데이터 가져오기 실패:', response.data.message);
      }
    } catch (error) {
      console.error('통계 데이터 가져오기 중 오류 발생:', error);
      console.error('오류 세부 정보:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };
  
  // 정렬 함수
  const sortData = (data, config) => {
    if (!config.key) return data;
    
    return [...data].sort((a, b) => {
      const valueA = parseFloat(a[config.key]) || 0;
      const valueB = parseFloat(b[config.key]) || 0;
      
      if (config.direction === 'asc') {
        return valueA - valueB;
      } else {
        return valueB - valueA;
      }
    });
  };
  
  // 정렬 요청 처리 함수
  const requestSort = (key) => {
    let direction = 'desc';
    
    // 이미 같은 컬럼으로 정렬 중이라면 방향 전환
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    
    setSortConfig({ key, direction });
    
    // 현재 데이터 재정렬
    const sortedData = sortData([...agentBettingList.agents_list], { key, direction });
    
    // 선택된 에이전트를 찾아서 맨 위로 올리기 (정렬 우선)
    if (filters.agentId) {
      const selectedAgentIndex = sortedData.findIndex(agent => agent.agent_id === filters.agentId);
      if (selectedAgentIndex > 0) { // 이미 맨 위에 있지 않은 경우에만
        const selectedAgent = sortedData.splice(selectedAgentIndex, 1)[0];
        sortedData.unshift(selectedAgent);
      }
    }
    
    setAgentBettingList({
      ...agentBettingList,
      agents_list: sortedData
    });
  };

  // 에이전트 베팅 리스트 가져오기 함수
  const fetchAgentBettingList = async () => {
    if (!filters.agentId) {
      return;
    }

    setLoadingAgentList(true);
    try {
      const startDateFormatted = filters.startDate
        ? filters.startDate.format('YYYY-MM-DD')
        : dayjs().subtract(7, 'day').format('YYYY-MM-DD');

      const endDateFormatted = filters.endDate
        ? filters.endDate.format('YYYY-MM-DD')
        : dayjs().format('YYYY-MM-DD');

      console.log('에이전트 베팅 리스트 요청 파라미터:', {
        agent_id: filters.agentId,
        start: startDateFormatted,
        end: endDateFormatted
      });

      const response = await axios.get('/agent/betting/statistics/list', {
        params: {
          agent_id: filters.agentId,
          start: startDateFormatted,
          end: endDateFormatted
        },
      });

      console.log('에이전트 베팅 리스트 API 응답:', response.data);

      if (response.data.success) {
        // 에이전트 리스트 받아오기
        let agentsList = [...response.data.agents_list];
        
        // 현재 정렬 설정으로 정렬
        agentsList = sortData(agentsList, sortConfig);
        
        // 선택된 에이전트를 찾아서 맨 위로 올리기
        if (filters.agentId) {
          const selectedAgentIndex = agentsList.findIndex(agent => agent.agent_id === filters.agentId);
          if (selectedAgentIndex > 0) { // 이미 맨 위에 있지 않은 경우에만
            const selectedAgent = agentsList.splice(selectedAgentIndex, 1)[0];
            agentsList.unshift(selectedAgent);
          }
        }
        
        // 정렬된 에이전트 리스트로 상태 업데이트
        setAgentBettingList({
          ...response.data,
          agents_list: agentsList
        });
      } else {
        console.error('에이전트 베팅 리스트 가져오기 실패:', response.data.message);
      }
    } catch (error) {
      console.error('에이전트 베팅 리스트 가져오기 중 오류 발생:', error);
      console.error('오류 세부 정보:', error.response?.data);
    } finally {
      setLoadingAgentList(false);
    }
  };

  // 에이전트 ID 변경 시 두 API 모두 호출
  useEffect(() => {
    if (filters.agentId) {
      fetchStatistics();
      fetchAgentBettingList();
    }
  }, [filters.agentId]);

  // includeChildren 변경 시 통계 API만 호출
  useEffect(() => {
    if (filters.agentId) {
      fetchStatistics();
    }
  }, [filters.includeChildren]);

  const handleFilterChange = (field, value) => {
    console.log(`필터 변경: ${field}`, value);
    setFilters(prevFilters => ({
      ...prevFilters,
      [field]: value,
    }));
  };

  const handleSearch = () => {
    console.log('검색 시작 날짜:', filters.startDate.format('YYYY-MM-DD'));
    console.log('검색 종료 날짜:', filters.endDate.format('YYYY-MM-DD'));
    console.log('하부 합산:', filters.includeChildren);
    fetchStatistics();
    fetchAgentBettingList();
  };

  // 페이지 변경 핸들러
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // 보여지는 행 개수 변경 핸들러
  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // 에이전트 베팅 리스트 페이지 변경 핸들러
  const handleChangeAgentListPage = (event, newPage) => {
    setAgentListPage(newPage);
  };
  
  // 에이전트 베팅 리스트 행 개수 변경 핸들러
  const handleChangeAgentListRowsPerPage = event => {
    setAgentListRowsPerPage(parseInt(event.target.value, 10));
    setAgentListPage(0);
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);

  const handleMenuOpen = (event, rowId) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowId(rowId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRowId(null);
  };

  const handleMenuItemClick = action => {
    if (action === 'edit') {
      navigate(`/agent/update/${selectedRowId}`);
    }

    if (action === 'history') {
      navigate(`/money-send-history?agentId=${selectedRowId}`);
    }

    if (action === 'charge') {
      navigate(`/money-charge-history?agentId=${selectedRowId}`);
    }

    if (action === 'user') {
      navigate(`/user-list?agentId=${selectedRowId}`);
    }

    if (action === 'payment') {
      navigate(`/money?agentId=${selectedRowId}`);
    }
    
    handleMenuClose();
  };

  const handleDetailClick = agentId => {
    navigate(`/statistic-agent-detail/${agentId}`, {
      state: {
        startDate: filters.startDate.format('YYYY-MM-DD'),
        endDate: filters.endDate.format('YYYY-MM-DD'),
      },
    });
  };

  // 금액 포맷팅 함수
  const formatNumber = (number, invertSign = true) => {
    if (!number) return '0';
    
    const num = parseFloat(number);
    const absNum = Math.abs(num);
    const formattedNum = absNum.toLocaleString('ko-KR');
    
    if (invertSign) {
      // 부호 반전: 양수는 음수로, 음수는 양수로
      return num === 0 ? '0' : (num > 0 ? `-${formattedNum}` : `${formattedNum}`);
    } else {
      // 기존 부호 유지
      return num === 0 ? '0' : (num < 0 ? `-${formattedNum}` : `${formattedNum}`);
    }
  };

  // 수익 계산 함수 (배팅 + 당첨 = 수익)
  const calculateProfit = (betAmount, winAmount) => {
    const bet = parseFloat(betAmount) || 0;
    const win = parseFloat(winAmount) || 0;
    return bet + win;
  };
  
  // 정렬 방향 아이콘 표시 함수
  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? '▲' : '▼';
    }
    return null;
  };
  
  // 커스텀 날짜 선택 컴포넌트
  const CustomDatePicker = ({ label, value, onChange }) => {
    return (
      <DatePicker
        label={label}
        value={value}
        onChange={(newValue) => {
          if (!newValue) return;
          console.log(`${label} 변경:`, newValue.format('YYYY-MM-DD'));
          onChange(newValue);
        }}
        format="YYYY-MM-DD"
        slotProps={{
          textField: {
            fullWidth: true,
            sx: {
              '& .MuiInputBase-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                color: '#fff',
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.15)',
              },
              '& .MuiSvgIcon-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
            }
          },
          popper: {
            sx: {
              '& .MuiPaper-root': {
                backgroundColor: 'rgba(35, 35, 50, 0.95)',
                color: '#fff',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
              }
            }
          }
        }}
      />
    );
  };

  // 디버깅 로그
  useEffect(() => {
    console.log('에이전트 베팅 리스트 데이터 업데이트:', agentBettingList);
    console.log('에이전트 리스트 데이터 길이:', agentBettingList.agents_list?.length || 0);
  }, [agentBettingList]);

  return (
    <Box sx={{
      p: 2,
      background: 'linear-gradient(180deg, rgba(25,25,38,0.95) 0%, rgba(25,32,48,0.95) 100%)',
      minHeight: '100vh',
    }}>
      <Paper sx={{
        p: 3,
        background: 'linear-gradient(145deg, rgba(35,35,50,0.9) 0%, rgba(35,45,65,0.85) 100%)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        backdropFilter: 'blur(8px)',
      }}>
        <Typography variant="h4" sx={{
          color: '#fff',
          fontSize: '28px',
          fontWeight: 'bold',
          mb: 1,
          letterSpacing: '0.5px',
          textShadow: '0 2px 4px rgba(0,0,0,0.3)',
        }}>
          에이전트별 수익 통계
        </Typography>
        <Typography variant="body2" sx={{
          color: 'rgba(255, 255, 255, 0.7)',
          mb: 3,
        }}>
          소수점 자리는 첫째 자리에서 반올림 되어집니다. 매일 1시간 마다 업데이트 되어집니다. 최대 2달까지 보관 됩니다.
        </Typography>

        {/* 필터 영역 */}
        <Box display="flex" flexDirection={isNonMobile ? 'row' : 'column'} gap={2} sx={{ mb: 3 }}>
          <FormControl fullWidth sx={{
            '& .MuiInputLabel-root': {
              color: 'rgba(255, 255, 255, 0.7)',
            },
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '8px',
              color: '#fff',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.15)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.3)',
            },
            '& .MuiSelect-icon': {
              color: 'rgba(255, 255, 255, 0.7)',
            },
          }}>
            <InputLabel>에이전트 아이디</InputLabel>
            <Select
              value={filters.agentId}
              onChange={e => handleFilterChange('agentId', e.target.value)}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: 'rgba(35, 35, 50, 0.95)',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                  },
                },
              }}
            >
              {agentList.map(agent => (
                <MenuItem 
                  key={agent.id} 
                  value={agent.id} 
                  sx={{
                    '&.MuiMenuItem-root': {
                      color: agent.grade === 'admin' ? '#ce93d8' : '#fff',
                      fontWeight: agent.grade === 'admin' ? 'bold' : 'normal',
                      backgroundColor: 'rgba(35, 35, 50, 0.95)',
                      '&:hover': {
                        backgroundColor: agent.grade === 'admin' ? 'rgba(206, 147, 216, 0.1)' : 'rgba(255, 255, 255, 0.1)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: agent.grade === 'admin' ? 'rgba(206, 147, 216, 0.2)' : 'rgba(96, 165, 250, 0.2)',
                        '&:hover': {
                          backgroundColor: agent.grade === 'admin' ? 'rgba(206, 147, 216, 0.3)' : 'rgba(96, 165, 250, 0.3)',
                        },
                      },
                    },
                  }}
                >
                  {currentUser?.grade === 'admin' && agent.level === 0 
                    ? '에이전트 아이디를 선택하세요' 
                    : agent.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
              <CustomDatePicker label="시작 날짜" value={filters.startDate} onChange={date => handleFilterChange('startDate', date)} />
              <CustomDatePicker label="종료 날짜" value={filters.endDate} onChange={date => handleFilterChange('endDate', date)} />
            </Box>
          </LocalizationProvider>

          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={loading || loadingAgentList}
            sx={{
              backgroundColor: '#60a5fa',
              color: '#fff',
              borderRadius: '10px',
              padding: '10px 24px',
              boxShadow: '0 4px 10px rgba(33, 150, 243, 0.3)',
              fontWeight: 'bold',
              textTransform: 'none',
              minWidth: '120px',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: '#3b82f6',
                boxShadow: '0 6px 12px rgba(30, 136, 229, 0.4)',
                transform: 'translateY(-2px)',
              },
              height: isNonMobile ? 'auto' : 56,
              alignSelf: isNonMobile ? 'flex-start' : 'stretch',
            }}
          >
            {(loading || loadingAgentList) ? <CircularProgress size={20} color="inherit" /> : <SearchIcon sx={{ fontSize: 20, mr: 1 }} />}
            {(loading || loadingAgentList) ? '로딩 중...' : '검색'}
          </Button>
        </Box>

        {/* 하부 합산 스위치 */}
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={filters.includeChildren}
                onChange={(e) => handleFilterChange('includeChildren', e.target.checked)}
                color="secondary"
              />
            }
            label="하부 합산"
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              '& .MuiSwitch-root': {
                '& .MuiSwitch-track': {
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                },
                '& .MuiSwitch-switchBase': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&.Mui-checked': {
                    color: '#ce93d8',
                  },
                  '&.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#ba68c8',
                    opacity: 0.5,
                  },
                },
              },
            }}
          />
        </Box>

        {/* 총 요약 정보 */}
        <Box sx={{
          mb: 3,
          p: 2,
          bgcolor: 'rgba(156, 39, 176, 0.15)',
          borderRadius: 2,
          border: '1px solid rgba(156, 39, 176, 0.2)',
        }}>
          <Typography variant="h5" sx={{
            fontWeight: 'bold',
            color: '#ce93d8',
            letterSpacing: '0.5px',
            mb: 1,
          }}>
            {filters.agentId ? 
              `${filters.agentId} 에이전트 총 합계 통계${filters.includeChildren ? ' (하부 포함)' : ''}` : 
              '에이전트를 선택해주세요'}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
            <Box sx={{
              minWidth: 150,
              p: 1,
              borderRadius: 1,
              bgcolor: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.05)',
            }}>
              <Typography variant="subtitle2" sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.875rem',
              }}>
                베팅 건수
              </Typography>
              <Typography variant="h6" sx={{
                color: '#fff',
                fontWeight: 'bold',
                fontSize: '1.25rem',
                mt: 0.5,
              }}>
                {stats.total_summary.bet_count} 건
              </Typography>
            </Box>
            <Box sx={{
              minWidth: 150,
              p: 1,
              borderRadius: 1,
              bgcolor: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.05)',
            }}>
              <Typography variant="subtitle2" sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.875rem',
              }}>
                배팅 금액
              </Typography>
              <Typography variant="h6" sx={{
                color: '#fff',
                fontWeight: 'bold',
                fontSize: '1.25rem',
                mt: 0.5,
              }}>
                {formatNumber(stats.total_summary.total_bet)} 원
              </Typography>
            </Box>
            <Box sx={{
              minWidth: 150,
              p: 1,
              borderRadius: 1,
              bgcolor: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.05)',
            }}>
              <Typography variant="subtitle2" sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.875rem',
              }}>
                당첨 금액
              </Typography>
              <Typography variant="h6" sx={{
                color: '#fff',
                fontWeight: 'bold',
                fontSize: '1.25rem',
                mt: 0.5,
              }}>
                {formatNumber(stats.total_summary.total_win)} 원
              </Typography>
            </Box>
            <Box sx={{
              minWidth: 150,
              p: 1,
              borderRadius: 1,
              bgcolor: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.05)',
            }}>
              <Typography variant="subtitle2" sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.875rem',
              }}>
                수익 금액
              </Typography>
              <Typography variant="h6" sx={{
                color: '#fff',
                fontWeight: 'bold',
                fontSize: '1.25rem',
                mt: 0.5,
                color: calculateProfit(stats.total_summary.total_bet, stats.total_summary.total_win) >= 0 
                  ? '#4ade80' 
                  : '#f87171',
              }}>
                {formatNumber(calculateProfit(stats.total_summary.total_bet, stats.total_summary.total_win))} 원
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* 게임 유형별 통계 */}
        {stats.game_type_stats && stats.game_type_stats.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{
              color: '#fff',
              fontWeight: 'bold',
              mb: 1,
              mt: 3,
            }}>
              게임 유형별 통계
            </Typography>
            <TableContainer sx={{
              overflowX: 'auto',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              mb: 3,
            }}>
              <Table stickyHeader size="small">
                <TableHead sx={{
                  backgroundColor: 'rgba(45, 45, 65, 0.9)',
                  '& .MuiTableCell-head': {
                    color: '#fff',
                    fontWeight: 'bold',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                  },
                }}>
                  <TableRow>
                    <TableCell sx={{
                      color: '#fff',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                    }}>게임 유형</TableCell>
                    <TableCell sx={{
                      color: '#fff',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                    }}>베팅 건수</TableCell>
                    <TableCell sx={{
                      color: '#fff',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                    }}>배팅 금액</TableCell>
                    <TableCell sx={{
                      color: '#fff',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                    }}>당첨 금액</TableCell>
                    <TableCell sx={{
                      color: '#fff',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                    }}>수익 금액</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.game_type_stats.map((row, index) => {
                    const profit = calculateProfit(row.total_bet, row.total_win);
                    return (
                      <TableRow key={index} sx={{
                        '&:nth-of-type(odd)': {
                          backgroundColor: 'rgba(255, 255, 255, 0.02)',
                        },
                        '&:nth-of-type(even)': {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          transition: 'background-color 0.3s ease',
                        },
                        '& .MuiTableCell-body': {
                          color: '#fff',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                        },
                      }}>
                        <TableCell sx={{
                          color: '#fff',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                        }}>{row.game_type}</TableCell>
                        <TableCell sx={{
                          color: '#fff',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                        }}>{row.bet_count}</TableCell>
                        <TableCell sx={{
                          color: '#fff',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                        }}>{formatNumber(row.total_bet)} 원</TableCell>
                        <TableCell sx={{
                          color: '#fff',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                        }}>{formatNumber(row.total_win)} 원</TableCell>
                        <TableCell
                          sx={{
                            color: profit >= 0 ? '#4ade80' : '#f87171',
                            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                          }}
                        >
                          {formatNumber(profit)} 원
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* 게임사별 통계 */}
        {stats.game_vendor_stats && stats.game_vendor_stats.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{
              color: '#fff',
              fontWeight: 'bold',
              mb: 1,
              mt: 3,
            }}>
              게임사별 통계
            </Typography>
            <TableContainer sx={{
              overflowX: 'auto',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              mb: 3,
            }}>
              <Table stickyHeader size="small">
                <TableHead sx={{
                  backgroundColor: 'rgba(45, 45, 65, 0.9)',
                  '& .MuiTableCell-head': {
                    color: '#fff',
                    fontWeight: 'bold',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                  },
                }}>
                  <TableRow>
                    <TableCell sx={{
                      color: '#fff',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                    }}>게임사</TableCell>
                    <TableCell sx={{
                      color: '#fff',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                    }}>베팅 건수</TableCell>
                    <TableCell sx={{
                      color: '#fff',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                    }}>배팅 금액</TableCell>
                    <TableCell sx={{
                      color: '#fff',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                    }}>당첨 금액</TableCell>
                    <TableCell sx={{
                      color: '#fff',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                    }}>수익 금액</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.game_vendor_stats.map((row, index) => {
                    const profit = calculateProfit(row.total_bet, row.total_win);
                    return (
                      <TableRow key={index} sx={{
                        '&:nth-of-type(odd)': {
                          backgroundColor: 'rgba(255, 255, 255, 0.02)',
                        },
                        '&:nth-of-type(even)': {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          transition: 'background-color 0.3s ease',
                        },
                        '& .MuiTableCell-body': {
                          color: '#fff',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                        },
                      }}>
                        <TableCell sx={{
                          color: '#fff',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                        }}>{row.game_vendor}</TableCell>
                        <TableCell sx={{
                          color: '#fff',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                        }}>{row.bet_count}</TableCell>
                        <TableCell sx={{
                          color: '#fff',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                        }}>{formatNumber(row.total_bet)} 원</TableCell>
                        <TableCell sx={{
                          color: '#fff',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                        }}>{formatNumber(row.total_win)} 원</TableCell>
                        <TableCell
                          sx={{
                            color: profit >= 0 ? '#4ade80' : '#f87171',
                            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                          }}
                        >
                          {formatNumber(profit)} 원
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* 에이전트 베팅 리스트 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{
            color: '#fff',
            fontWeight: 'bold',
            mb: 2,
            fontSize: '1.25rem',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            paddingBottom: '8px',
          }}>
            에이전트 베팅 리스트
          </Typography>
          
          {loadingAgentList ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress sx={{ color: '#60a5fa', margin: '40px 0' }} />
            </Box>
          ) : agentBettingList.agents_list && agentBettingList.agents_list.length > 0 ? (
            <TableContainer sx={{
              overflowX: 'auto',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              mb: 3,
            }}>
              <Table stickyHeader>
                <TableHead sx={{
                  backgroundColor: 'rgba(45, 45, 65, 0.9)',
                  '& .MuiTableCell-head': {
                    color: '#fff',
                    fontWeight: 'bold',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                  },
                }}>
                  <TableRow>
                    <TableCell sx={{
                      color: '#fff',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                    }}>에이전트 ID</TableCell>
                    <TableCell sx={{
                      color: '#fff',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                    }}>등급</TableCell>
                    <TableCell 
                      onClick={() => requestSort('total_bet')}
                      sx={{
                        color: '#fff',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                        cursor: 'pointer',
                        position: 'relative',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        }
                      }}
                    >
                      배팅 금액
                      {sortConfig.key === 'total_bet' && (
                        <Box component="span" sx={{ 
                          display: 'inline-block',
                          ml: 1,
                          color: 'rgba(255, 255, 255, 0.7)',
                          fontSize: '0.75rem'
                        }}>
                          {sortConfig.direction === 'asc' ? '▲' : '▼'}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell 
                      onClick={() => requestSort('total_win')}
                      sx={{
                        color: '#fff',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                        cursor: 'pointer',
                        position: 'relative',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        }
                      }}
                    >
                      당첨 금액
                      {sortConfig.key === 'total_win' && (
                        <Box component="span" sx={{ 
                          display: 'inline-block',
                          ml: 1,
                          color: 'rgba(255, 255, 255, 0.7)',
                          fontSize: '0.75rem'
                        }}>
                          {sortConfig.direction === 'asc' ? '▲' : '▼'}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell 
                      onClick={() => requestSort('total_profit')}
                      sx={{
                        color: '#fff',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                        cursor: 'pointer',
                        position: 'relative',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        }
                      }}
                    >
                      수익 금액
                      {sortConfig.key === 'total_profit' && (
                        <Box component="span" sx={{ 
                          display: 'inline-block',
                          ml: 1,
                          color: 'rgba(255, 255, 255, 0.7)',
                          fontSize: '0.75rem'
                        }}>
                          {sortConfig.direction === 'asc' ? '▲' : '▼'}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {agentBettingList.agents_list
                    .slice(agentListPage * agentListRowsPerPage, agentListPage * agentListRowsPerPage + agentListRowsPerPage)
                    .map((agent, index) => {
                      const profit = calculateProfit(agent.total_bet, agent.total_win);
                      return (
                        <TableRow key={index} sx={{
                          '&:nth-of-type(odd)': {
                            backgroundColor: 'rgba(255, 255, 255, 0.02)',
                          },
                          '&:nth-of-type(even)': {
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            transition: 'background-color 0.3s ease',
                          },
                          '& .MuiTableCell-body': {
                            color: '#fff',
                            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                          },
                        }}>
                          <TableCell sx={{
                            color: '#fff',
                            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                          }}>
                            <Typography variant="body1" sx={{ color: '#fff' }}>
                              {agent.agent_id}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{
                            color: '#fff',
                            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                          }}>
                            <Box sx={{ 
                              display: 'inline-block', 
                              px: 1, 
                              py: 0.5, 
                              borderRadius: 1, 
                              bgcolor: agent.grade === 'admin' ? 'rgba(156, 39, 176, 0.2)' : 'rgba(96, 165, 250, 0.2)',
                              color: agent.grade === 'admin' ? '#ce93d8' : '#60a5fa',
                              fontWeight: 'bold',
                              fontSize: '0.75rem',
                              textTransform: 'uppercase'
                            }}>
                              {agent.grade}
                            </Box>
                          </TableCell>
                          <TableCell sx={{
                            color: '#fff',
                            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                          }}>{formatNumber(agent.total_bet)} 원</TableCell>
                          <TableCell sx={{
                            color: '#fff',
                            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                          }}>{formatNumber(agent.total_win)} 원</TableCell>
                          <TableCell
                            sx={{
                              color: profit >= 0 ? '#4ade80' : '#f87171',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                              fontWeight: 'bold'
                            }}
                          >
                            {formatNumber(profit)} 원
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[10, 20, 30, 50]}
                component="div"
                count={agentBettingList.agents_list.length}
                rowsPerPage={agentListRowsPerPage}
                page={agentListPage}
                onPageChange={handleChangeAgentListPage}
                onRowsPerPageChange={handleChangeAgentListRowsPerPage}
                labelRowsPerPage=""
                sx={{
                  marginTop: '12px',
                  borderRadius: '12px',
                  backgroundColor: 'rgba(45, 45, 65, 0.8)',
                  color: '#fff',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  '& .MuiTablePagination-toolbar': {
                    justifyContent: 'center',
                  },
                  '& .MuiTablePagination-select': {
                    padding: '6px 10px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    color: '#fff',
                    fontWeight: 'bold',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                    '& .MuiSelect-icon': {
                      color: 'rgba(255, 255, 255, 0.7)',
                    },
                  },
                  '& .MuiTablePagination-actions': {
                    color: '#fff',
                    '& .MuiIconButton-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                      '&.Mui-disabled': {
                        color: 'rgba(255, 255, 255, 0.3)',
                      },
                    },
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      sx: {
                        backgroundColor: 'rgba(35, 35, 50, 0.95)',
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        '& .MuiMenuItem-root': {
                          color: '#fff',
                          padding: '8px 16px',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(96, 165, 250, 0.2)',
                          },
                        },
                      },
                    },
                  },
                }}
              />
            </TableContainer>
          ) : (
            <Box sx={{ 
              py: 4, 
              textAlign: 'center', 
              backgroundColor: 'rgba(35, 35, 50, 0.5)', 
              borderRadius: 2,
              border: '1px dashed rgba(255, 255, 255, 0.1)'
            }}>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                에이전트를 선택하고 검색 버튼을 클릭하여 베팅 리스트를 확인하세요.
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* 메뉴 */}
      <Menu 
        anchorEl={anchorEl} 
        open={Boolean(anchorEl)} 
        onClose={handleMenuClose} 
        sx={{
          '& .MuiPaper-root': {
            backgroundColor: 'rgba(35, 35, 50, 0.98)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            width: '150px',
            color: '#fff',
          },
          '& .MuiMenuItem-root': {
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          },
          '& .MuiDivider-root': {
            borderColor: 'rgba(255, 255, 255, 0.1)',
          },
        }}
      >
        <MenuItem onClick={() => handleMenuItemClick('edit')}>수정</MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('history')}>지급 내역</MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('charge')}>충전 내역</MenuItem>
        <Box sx={{
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          mt: 1,
          pt: 1,
        }}>
          <Typography variant="subtitle2" sx={{
            pl: 2,
            color: '#ce93d8',
            fontWeight: 'bold',
          }}>
            유저
          </Typography>
          <MenuItem onClick={() => handleMenuItemClick('user')}>유저 목록</MenuItem>
          <MenuItem onClick={() => handleMenuItemClick('payment')}>지급 내역</MenuItem>
        </Box>
      </Menu>
    </Box>
  );
};

export default StatisticAgent;